// filepath: apps/web/src/features/admin/MatrixConflictDialog.tsx
//
// FR-UI-ADM-1 AC-4 — modal dialog that surfaces the FR-UI-CORE-6
// refetch / merge / discard UX when the server returns HTTP 409
// ROLE_VERSION_MISMATCH.
//
// Non-skippable: the only way out of this dialog is one of the three
// action buttons (or the explicit Cancel button). There is no
// auto-retry on the mutation side — INV-7.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { VersionConflictError } from "./types";

interface MatrixConflictDialogProps {
  open: boolean;
  conflict: VersionConflictError | null;
  onRefetch: () => void;
  onMerge: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export function MatrixConflictDialog({
  open,
  conflict,
  onRefetch,
  onMerge,
  onDiscard,
  onClose,
}: MatrixConflictDialogProps): React.ReactElement | null {
  const { t } = useTranslation();

  if (!open || conflict === null) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="matrix-conflict-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="matrix-conflict-dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="matrix-conflict-title" className="text-sm font-semibold">
          {t("admin.errors.version_conflict.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("admin.errors.version_conflict.body")}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Server version: {conflict.currentVersion}
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="matrix-conflict-close"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onRefetch}
            data-testid="matrix-conflict-refetch"
          >
            {t("admin.errors.version_conflict.refetch")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onMerge}
            data-testid="matrix-conflict-merge"
          >
            {t("admin.errors.version_conflict.merge")}
          </button>
          <button
            type="button"
            className="rounded bg-destructive px-3 py-1 text-xs text-destructive-foreground"
            onClick={onDiscard}
            data-testid="matrix-conflict-discard"
          >
            {t("admin.errors.version_conflict.discard")}
          </button>
        </div>
      </div>
    </div>
  );
}
